/**
 * The seven multi-entity link pickers, shared by the task and the work
 * report form (ME3a).
 *
 * Both forms ask the same question — "what is this attached to?" — so
 * they render the same block rather than two that drift. Each type gets
 * one ``AsyncMultiCombobox`` over the MANUAL id set, plus a note naming
 * the links the backend derived from those choices.
 *
 * **The form edits manual links only.** A derived link is a consequence
 * of a manual one (a tenancy contributes its tenant, room, unit and
 * property), so it is shown as information and never as a selected chip.
 * Offering it as a removable chip would promise something the write path
 * cannot honour: removing it would only make it come back on the next
 * recompute, because its source is still selected.
 *
 * The derived note is read from the SAVED row. It therefore describes
 * what the last save produced, not a live preview of what an unsaved
 * change would produce — the server owns derivation and no rule for it
 * is duplicated here.
 */
import React from "react";

import AsyncMultiCombobox from "../../../components/ui/AsyncMultiCombobox";
import {
  ENTITY_LINK_TYPES,
  ENTITY_LINK_TYPES_BY_NAME,
  knownOptionsFor,
} from "./multiEntityLinks";

/**
 * The links of one type that the backend derived and the person did not
 * pick, each with the selection it came from.
 *
 * A link that is manual AND derived is excluded: it is already a chip
 * above, and listing it twice would read as two links.
 */
export function derivedNotesFor(row, descriptor) {
  const array = row?.[descriptor.arrayField];
  if (!Array.isArray(array)) return [];
  return array
    .filter((link) => link && !link.is_manual && link.id)
    .map((link) => ({
      id: String(link.id),
      label: (link.label || "").trim() || "Ohne Bezeichnung",
      sources: (link.derived_from || [])
        .map((src) => {
          const from = ENTITY_LINK_TYPES_BY_NAME[src?.entity_type];
          return from ? from.groupLabel : null;
        })
        .filter(Boolean),
    }));
}

/** "aus Mietverhältnisse" / "aus Mietverhältnisse, Zimmer" / "" */
function sourceSuffix(sources) {
  const unique = Array.from(new Set(sources || []));
  return unique.length ? ` — aus ${unique.join(", ")}` : "";
}

export default function MultiEntityLinkFields({
  /** The manual id sets, keyed by ``*_ids`` field name. */
  value,
  /** (idsField: string, nextIds: string[]) => void */
  onChange,
  /** The saved row — supplies chip labels and the derived note. */
  row = null,
  disabled = false,
  /** Restrict to a subset of types; defaults to all seven. */
  types = null,
}) {
  const descriptors = React.useMemo(
    () =>
      types
        ? ENTITY_LINK_TYPES.filter((d) => types.includes(d.type))
        : ENTITY_LINK_TYPES,
    [types]
  );

  return (
    <div className="space-y-3">
      {descriptors.map((d) => {
        const derived = derivedNotesFor(row, d);
        return (
          <AsyncMultiCombobox
            key={d.type}
            label={d.fieldLabel}
            placeholder="— keine —"
            searchPlaceholder={d.searchPlaceholder}
            loadingText={d.loadingText}
            emptyText={d.emptyText}
            errorText={d.errorText}
            value={value?.[d.idsField] || []}
            selectedItems={knownOptionsFor(row, d)}
            onChange={(next) => onChange(d.idsField, next)}
            fetchPage={d.fetchPage}
            getItemId={(item) => item.id}
            getItemLabel={(item) => d.itemLabel(item)}
            disabled={disabled}
            hydrateById={d.fetchById || null}
            renderItem={(item) => {
              const primary = d.itemLabel(item);
              const secondary = d.itemDetail ? d.itemDetail(item) : null;
              return (
                <span className="block min-w-0">
                  <span
                    title={primary}
                    className="block truncate text-[13px] font-medium text-foreground"
                  >
                    {primary}
                  </span>
                  {secondary ? (
                    <span
                      title={secondary}
                      className="block truncate text-[11px] text-muted-foreground"
                    >
                      {secondary}
                    </span>
                  ) : null}
                </span>
              );
            }}
            footer={
              derived.length ? (
                <p className="m-0 mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                  <span className="font-medium">Automatisch verknüpft: </span>
                  {derived
                    .map((x) => `${x.label}${sourceSuffix(x.sources)}`)
                    .join(" · ")}
                </p>
              ) : null
            }
          />
        );
      })}
    </div>
  );
}
